import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "arbe_cart";

// Cart entries are uniquely keyed by product_id + variant_id (so the same product
// in different sizes counts as separate line-items).
const cartKey = (product_id, variant_id) => `${product_id}__${variant_id || "default"}`;

export function CartProvider({ children }) {
  // Lazy initializer reads localStorage synchronously, avoiding a mount-time
  // race where the save effect would overwrite persisted data with [].
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // `variant` (optional) is one of the entries from product.variants
  const add = (product, qty = 1, variant = null) => {
    const variant_id = variant?.id || null;
    const key = cartKey(product.id, variant_id);
    setItems((prev) => {
      const found = prev.find((i) => i.key === key);
      if (found) {
        return prev.map((i) =>
          i.key === key ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          variant_id,
          variant_label: variant?.label || null,
          variant_unit: variant?.unit || null,
          name: product.name,
          price: variant ? variant.price : product.price,
          image_url: product.image_url,
          quantity: qty,
          unit: variant?.unit || product.unit,
          stock: product.stock,
          category: product.category,
        },
      ];
    });
  };

  const update = (key, quantity) => {
    if (quantity <= 0) return remove(key);
    setItems((prev) =>
      prev.map((i) => (i.key === key ? { ...i, quantity } : i))
    );
  };

  const remove = (key) =>
    setItems((prev) => prev.filter((i) => i.key !== key));

  const clear = () => setItems([]);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, add, update, remove, clear, subtotal, count }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
